import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Box, Paper, LinearProgress, Typography, Select, MenuItem } from "@mui/material";
import {
  DataGrid,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridRootProps,
  isLeaf,
  gridColumnLookupSelector,
  useGridSelector,
  GridColumnsPanel,
} from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import useSWR from 'swr';
// import ProductionStatusDetails from './ProductionStatusDetails';
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import api from '../../../services/api';

const EpisodeGrid = () => {
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(null);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [showEpisodeColumns, setShowEpisodeColumns] = useState(false);

  const { data: productions, error: productionsError } = useSWR('/productions');
  const { data: categoryItems, error: categoryError } = useSWR(
    selectedProduction?.id ? `/production-category-item/of-production/${selectedProduction.id}` : null
  );

  const { data: itemsDetails, error: detailsError } = useSWR(
    selectedProduction?.id && categoryItems
      ? `/expense/test/${selectedProduction.id}/${categoryItems.map((item) => item.id).join(",")}`
      : null,
    async () => {
      try {
        const itemRequests = categoryItems.map((item) =>
          api.get(`/expense/test/${selectedProduction.id}/${item.id}`)
            .catch((error) => {
              console.error(`Error fetching item ${item.id}:`, error);
              return null; // Return null for this item if the fetch fails
            })
        );

        const results = await Promise.all(itemRequests);

        // Filter out null responses to avoid processing invalid data
        return results.filter((item) => item !== null);
      } catch (error) {
        console.error("Error fetching items:", error);
        throw error; // Let SWR handle the error
      }
    },
    { refreshInterval: 3700 }
  );


  useEffect(() => {
    const savedProduction = localStorage.getItem('selectedProduction');
    if (savedProduction && productions) {
      const parsedProduction = JSON.parse(savedProduction);
      const production = productions.find(p => p.id === parsedProduction.id);
      if (production) {
        setSelectedProduction(production);
      }
    }
  }, [productions]);

  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem('selectedProduction', JSON.stringify(selectedProduction));
    }
  }, [selectedProduction]);

  const handleCellClick = (params) => {
    const episodeData = params.value;
    if (!episodeData) {
      return;
    }
    if (params.formattedValue.amount === null) {
      return;
    }

    setSelectedItem(params.row.item_db_id);
    setSelectedColumn(Number(params.field.split('').filter(char => /\d/.test(char)).join('')));
    setSelectedItemName(params.row.item);
    setSelectedBudget(params.row.budget);
  };

  const handlePanelClose = () => {
    setSelectedItem(null);
    setSelectedColumn(null);
    setSelectedItemName(null);
  };

  const handleProductionChange = (event) => {
    setSelectedProduction(productions.find((p) => p.id === event.target.value));
  };

  const { columns, rows, columnGroupingModel } = useMemo(() => {
    if (!selectedProduction || !categoryItems || !itemsDetails)
      return { columns: [], rows: [], columnGroupingModel: [] };

    const maxEpisodes = selectedProduction.no_of_episodes;

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '';
      return new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const gridRows = categoryItems.map((item) => {
      const itemDetailsList = itemsDetails.flat().filter(
        (detail) => detail.pci_id === item.id
      );

      const episodeData = Object.fromEntries(
        Array.from({ length: maxEpisodes }, (_, i) => [
          `episode-${i + 1}`,
          { amount: 0, expPred: false, maxEpAm: 0 }
        ])
      );

      let budget = '';
      const categoryItem = categoryItems.find((category) => category.id === item.id);
      if (categoryItem) {
        budget = categoryItem.pci_amount;
      }

      itemDetailsList.forEach((itemDetail) => {
        itemDetail.exp_assigned_episodes.forEach((assignedEpisode) => {
          const episodeKey = `episode-${assignedEpisode}`;
          if (itemDetail.exp_pred === true) {
            episodeData[episodeKey].expPred = true;
          }
          const expenses = itemDetail.expense_details.filter(
            (expense) => expense.exp_d_episode === assignedEpisode
          );
          const totalExpense = expenses.reduce(
            (sum, expense) => sum + (expense.exp_d_amount || 0),
            0
          );
          episodeData[episodeKey].amount += totalExpense;
          if (itemDetail.exp_assigned_episodes?.length === maxEpisodes) {
            episodeData[episodeKey].maxEpAm += totalExpense;
          }
        });
      });

      const totalCost = Object.values(episodeData).reduce(
        (sum, ep) => sum + (ep.amount || 0),
        0
      );
      const validEpisodeCount = Object.values(episodeData).filter(
        (ep) => ep.amount !== null
      ).length;
      const averageCost = validEpisodeCount > 0 ? totalCost / validEpisodeCount : 0;

      return {
        item_db_id: item.id,
        id: item.pci_aa,
        item: item.pci_descr,
        budget,
        formattedBudget: formatCurrency(budget),
        budgetTotal: budget * maxEpisodes,
        formattedBudgetTotal: formatCurrency(budget * maxEpisodes),
        average: averageCost,
        formattedAverage: formatCurrency(averageCost),
        totalItemActualCost: totalCost,
        formattedTotalItemActualCost: formatCurrency(totalCost),
        ...Object.fromEntries(
          Object.entries(episodeData).map(([key, value]) => [
            key,
            { amount: value.amount, exp_pred: value.expPred, maxEpAm: value.maxEpAm }
          ])
        ),
        ...Object.fromEntries(
          Object.entries(episodeData).map(([key, value]) => [
            `formatted_${key}`,
            formatCurrency(value.amount)
          ])
        ),
      };
    });

    const rowsWithLoss = gridRows.map((row) => {
      const loss = row.totalItemActualCost - row.budgetTotal;
      const percentage = row.budgetTotal ? (loss / row.budgetTotal) * 100 : 0;
      const displayValue = Math.min(Math.max(percentage, 0), 100);
      return {
        ...row,
        loss,
        formattedLoss: formatCurrency(loss),
        gainPercentage: displayValue,
      };
    });

    // Filter out rows where loss <= 0 (only show items with actual loss)
    const rowsWithPositiveLoss = rowsWithLoss.filter((row) => row.loss > 0);

    // Sort all rows by loss (descending) - display all records, not just top 10
    const sortedRows = rowsWithPositiveLoss.sort((a, b) => b.loss - a.loss);

    const accumulatedBudget = categoryItems.reduce(
      (sum, item) => sum + (parseFloat(item.pci_amount) || 0),
      0
    );
    const columnGroupingModel = showEpisodeColumns ? [
      ...Array.from({ length: maxEpisodes }, (_, i) => {
        const episodeSum = gridRows.reduce(
          (sum, row) => sum + (parseFloat(row[`episode-${i + 1}`]?.amount || 0)),
          0
        );
        const totalOfFullAssigned = gridRows.reduce(
          (sum, row) => sum + (parseFloat(row[`episode-${i + 1}`]?.maxEpAm) || 0),
          0
        );
        const projectionValue = totalOfFullAssigned * (maxEpisodes - i);
        const formattedProjection = new Intl.NumberFormat('en-IE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(projectionValue);
        const formattedEpisodeSum = new Intl.NumberFormat('en-IE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(episodeSum);
        const isExceeded = episodeSum > accumulatedBudget;
        const episodeSumStyle = isExceeded ? { color: 'red' } : { color: '#2bc12b' };

        return {
          groupId: `Ep. ${i + 1}`,
          headerAlign: 'right',
          headerName: formattedProjection,
          description: '',
          children: [
            {
              groupId: `Episode ${i + 1}`,
              headerAlign: 'right',
              headerName: <span style={episodeSumStyle}>{formattedEpisodeSum}</span>,
              description: ` `,
              children: [{ field: `episode-${i + 1}` }],
            },
          ],
        };
      }),
    ] : [];

    const episodeColumns = Array.from({ length: maxEpisodes }, (_, i) => ({
      field: `episode-${i + 1}`,
      headerName: `Episode ${i + 1}`,
      width: 120,
      valueGetter: (params) => params?.row?.[`episode-${i + 1}`]?.amount || 0,
      renderCell: (params) => {
        const formattedValue = params?.row?.[`formatted_episode-${i + 1}`];
        return <span>{formattedValue || ''}</span>;
      },
    }));

    const columns = [
      { field: 'id', hide: true },
      {
        field: 'item',
        headerAlign: 'center',
        headerName: 'Στοιχείο',
        flex: 1,
        minWidth: 300,
        maxWidth: 500,
        editable: false,
      },
      {
        field: 'budget',
        headerAlign: 'center',
        headerName: 'Προϋπολογισμός',
        flex: 1,
        minWidth: 160,
        maxWidth: 190,
        renderCell: (params) => (
          <span style={{ display: 'inline-block', textAlign: 'right', width: '100%' }}>
            {params.row.formattedBudget}
          </span>
        ),
      },
      {
        field: 'average',
        headerAlign: 'center',
        headerName: 'Μέσος Όρος',
        flex: 1,
        minWidth: 150,
        maxWidth: 200,
        renderCell: (params) => (
          <span style={{ display: 'inline-block', textAlign: 'right', width: '100%' }}>
            {params.row.formattedAverage}
          </span>
        ),
      },
      {
        field: 'budgetTotal',
        headerAlign: 'center',
        headerName: 'Σύνoλο Προϋπ.',
        flex: 1,
        minWidth: 180,
        maxWidth: 200,
        renderCell: (params) => (
          <span style={{ display: 'inline-block', textAlign: 'right', width: '100%' }}>
            {params.row.formattedBudgetTotal}
          </span>
        ),
      },
      {
        field: 'totalItemActualCost',
        headerAlign: 'center',
        headerName: 'Σύνολο Δαπάνης',
        flex: 1,
        minWidth: 150,
        maxWidth: 200,
        renderCell: (params) => (
          <span style={{ display: 'inline-block', textAlign: 'right', width: '100%' }}>
            {params.row.formattedTotalItemActualCost}
          </span>
        ),
      },
      {
        field: 'loss',
        headerAlign: 'center',
        headerName: 'Απώλεια',
        flex: 1,
        minWidth: 180,
        maxWidth: 200,
        renderCell: (params) => (
          <span style={{ display: 'inline-block', textAlign: 'right', width: '100%' }}>
            {params.row.formattedLoss}
          </span>
        ),
      },
      {
        field: 'gainPercentage',
        headerAlign: 'center',
        headerName: 'Απώλεια %',
        flex: 1,
        minWidth: 180,
        maxWidth: 220,
        renderCell: (params) => {
          const displayValue = params.row.gainPercentage;
          return (
            <Box display="flex" alignItems="center" width="100%">
              <Box width="100%" mr={1}>
                <LinearProgress variant="determinate" value={displayValue} color="error" />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="error">
                  {Math.round(displayValue)}%
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      ...(showEpisodeColumns ? episodeColumns : []),
    ];

    return {
      columns,
      rows: sortedRows,
      columnGroupingModel,
    };
  }, [selectedProduction, categoryItems, itemsDetails, showEpisodeColumns]);

  const isLoading = selectedProduction && (!categoryItems || !itemsDetails);

  if (productionsError || categoryError || detailsError) {
    return <Typography>Error loading data.</Typography>;
  }

  const getColumnGroupLeaves = (group, callback) => {
    group.children.forEach((child) => {
      if (isLeaf(child)) {
        callback(child.field);
      } else {
        getColumnGroupLeaves(child, callback);
      }
    });
  };

  function ColumnGroup({ group, columnLookup, apiRef, columnVisibilityModel }) {
    const leaves = React.useMemo(() => {
      const fields = [];
      getColumnGroupLeaves(group, (field) => fields.push(field));
      return fields;
    }, [group]);

    const { isGroupChecked, isGroupIndeterminate } = React.useMemo(() => {
      return {
        isGroupChecked: leaves.every(
          (field) => columnVisibilityModel[field] !== false,
        ),
        isGroupIndeterminate:
          leaves.some((field) => columnVisibilityModel[field] === false) &&
          !leaves.every((field) => columnVisibilityModel[field] === false),
      };
    }, [columnVisibilityModel, leaves]);

    const toggleColumnGroup = (checked) => {
      const newColumnVisibilityModel = {
        ...columnVisibilityModel,
      };
      getColumnGroupLeaves(group, (field) => {
        newColumnVisibilityModel[field] = checked;
      });
      apiRef.current.setColumnVisibilityModel(newColumnVisibilityModel);
    };

    const toggleColumn = (field, checked) => {
      apiRef.current.setColumnVisibility(field, checked);
    };

    return (
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={isGroupChecked}
              indeterminate={isGroupIndeterminate}
              size="small"
              sx={{ p: 1 }}
            />
          }
          label={group.headerName ?? group.groupId}
          onChange={(_, newValue) => toggleColumnGroup(newValue)}
        />
        <Box sx={{ pl: 3.5 }}>
          {group.children.map((child) => {
            return isLeaf(child) ? (
              <Stack direction="row" key={child.field}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={columnVisibilityModel[child.field] !== false}
                      size="small"
                      sx={{ p: 1 }}
                    />
                  }
                  label={columnLookup[child.field].headerName ?? child.field}
                  onChange={(_, newValue) => toggleColumn(child.field, newValue)}
                />
              </Stack>
            ) : (
              <ColumnGroup
                group={child}
                columnLookup={columnLookup}
                key={child.groupId}
                apiRef={apiRef}
                columnVisibilityModel={columnVisibilityModel}
              />
            );
          })}
        </Box>
      </div>
    );
  }

  function ColumnsPanel(props) {
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
    const columnVisibilityModel = useGridSelector(
      apiRef,
      gridColumnVisibilityModelSelector,
    );

    const columnGroupingModel = rootProps.columnGroupingModel;

    if (!columnGroupingModel) {
      return <GridColumnsPanel {...props} />;
    }

    return (
      <Box sx={{ px: 2, py: 0.5 }}>
        {columnGroupingModel.map((group) => (
          <ColumnGroup
            group={group}
            columnLookup={columnLookup}
            columnVisibilityModel={columnVisibilityModel}
            key={group.groupId}
            apiRef={apiRef}
          />
        ))}
      </Box>
    );
  }

  const columnsToExtract = [
    { field: 'item', headerName: 'Στοιχείο' },
    { field: 'formattedBudget', headerName: 'Προϋπολογισμός' },
    { field: 'formattedAverage', headerName: 'Μέσος Όρος' },
    { field: 'formattedBudgetTotal', headerName: 'Σύνoλο Προϋπ.' },
    { field: 'formattedTotalItemActualCost', headerName: 'Σύνολο Δαπάνης' },
  ];

  const handleExportToExcel = () => {
    exportToExcel(
      rows,
      columnsToExtract,
      `Ζημιογόνες Δαπάνες`
    );
  };

  const handleExportToPdf = () => {
    exportToPdf(
      rows,
      columnsToExtract,
      `Ζημιογόνες Δαπάνες`
    );
  };

  const handleExportToCsv = () => {
    exportToCsv(
      rows,
      columnsToExtract,
      `Ζημιογόνες Δαπάνες`
    );
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Category Items" module="Productions" subtitle="List of category items" />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryItem ? 8 : 12}>

          <FormListToolbar
            disableAdd={true}
            handleExportToExcel={handleExportToExcel}
            handleExportToPdf={handleExportToPdf}
            handleExportToCsv={handleExportToCsv}
          />

          <Box m="8px 0 0 0" height="80vh">
            <Paper style={{ padding: '1rem', height: '80vh', width: '100%' }}>

              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <FormControl size="small" style={{ width: '20%' }}>
                  <InputLabel id="prod-label">Παραγωγή</InputLabel>
                  <Select
                    labelId="prod-label"
                    id="prod-id"
                    label="Production"
                    fullWidth
                    size="small"
                    value={selectedProduction?.id || ''}
                    onChange={handleProductionChange}
                  >
                    {productions?.map((production) => (
                      <MenuItem key={production.id} value={production.id}>
                        {production.p_descr}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showEpisodeColumns}
                      onChange={(e) => setShowEpisodeColumns(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Εμφάνιση Επεισοδίων"
                />
              </Box>

              {/* Show loading indicator only after a production is selected */}
              {isLoading && <LinearProgress />}

              {/*If no prod. is selected yet*/}
              {!selectedProduction && (
                <DataGrid
                  sx={{
                    height: '66vh',
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #ddd',
                      borderBottom: 'none',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #ddd',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                    },
                  }}
                  rows={rows}
                  columns={[
                    {
                      field: 'item',
                      headerName: 'Στοιχεία',
                      flex: 1,
                      minWidth: 160,
                      maxWidth: 270,
                    },
                    {
                      field: 'budget',
                      headerName: 'Προϋπολογισμός',
                      flex: 1,
                      minWidth: 100,
                      maxWidth: 160,
                    },
                    {
                      field: 'budgetTotal',
                      headerName: 'Προϋπολογισμός',
                      flex: 1,
                      minWidth: 100,
                      maxWidth: 160,
                    },
                    {
                      field: 'episode-1',
                      headerName: 'Επεισόδια',
                      flex: 1,
                      minWidth: 100,
                      maxWidth: 160,
                    },
                  ]}
                  disableSelectionOnClick
                  style={{ overflow: 'hidden', width: '100%' }}
                />
              )}

              { /*Data Grid*/}
              {selectedProduction && categoryItems && !isLoading && (
                <DataGrid
                  sx={{
                    height: '70vh',
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #ddd',
                      borderBottom: 'none',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #ddd',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                    },
                  }}
                  columnGroupingModel={columnGroupingModel}
                  slots={{ columnsPanel: ColumnsPanel }}
                  rows={rows}
                  columns={columns}
                  onCellClick={handleCellClick}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'loss', sort: 'desc' }],
                    },
                  }}
                  columnVisibilityModel={{
                    id: false,
                  }}
                  style={{ overflow: 'hidden', width: '100%' }}
                />
              )}

            </Paper>
          </Box>
        </Grid>

      </Grid>
    </Box>
  );
};

export default EpisodeGrid;
